/**
 * Per-scene fixed-step driver for everything in src/characters. One instance per scene (lazily
 * created), hooked on the scene UPDATE event, torn down on SHUTDOWN. Order of stepping is fixed:
 * player controller (0) → actors (10) → followers (20) → NPC schedules (30).
 *
 * A world scene that runs its own FixedStepper can set `autoStep = false` and call `step(dtMs)` from
 * its own fixed loop instead (keeps a single simulation clock). Phaser-free at runtime (scene events
 * by name, see sceneEvents.ts) so it can be unit-tested in Node with a fake scene.
 */
import type Phaser from 'phaser';
import { FixedStepper } from '@/core/clock';
import { SceneEvents } from './sceneEvents';

export interface Steppable { step(dtMs: number): void }

export const StepOrder = { CONTROLLER: 0, ACTOR: 10, FOLLOWER: 20, SCHEDULE: 30 } as const;

const systems = new WeakMap<Phaser.Scene, ActorSystem>();

export class ActorSystem {
  readonly stepper = new FixedStepper();
  autoStep = true;
  /** Total fixed steps executed (diagnostics). */
  steps = 0;
  private entries: { s: Steppable; order: number; seq: number }[] = [];
  private sorted: Steppable[] = [];
  private seq = 0;
  private alive = true;
  private readonly onUpdate = (_time: number, delta: number) => { if (this.autoStep) this.stepper.tick(delta, (dt) => this.step(dt)); };
  private readonly onShutdown = () => this.dispose();

  private constructor(readonly scene: Phaser.Scene) {
    scene.events.on(SceneEvents.UPDATE, this.onUpdate);
    scene.events.once(SceneEvents.SHUTDOWN, this.onShutdown);
    scene.events.once(SceneEvents.DESTROY, this.onShutdown);
  }

  static get(scene: Phaser.Scene): ActorSystem {
    let s = systems.get(scene);
    if (!s || !s.alive) { s = new ActorSystem(scene); systems.set(scene, s); }
    return s;
  }
  static peek(scene: Phaser.Scene): ActorSystem | null { const s = systems.get(scene); return s && s.alive ? s : null; }

  add(s: Steppable, order: number): void {
    if (this.entries.some((e) => e.s === s)) return;
    this.entries.push({ s, order, seq: this.seq++ });
    this.resort();
  }
  remove(s: Steppable): void {
    const n = this.entries.length;
    this.entries = this.entries.filter((e) => e.s !== s);
    if (this.entries.length !== n) this.resort();
  }
  get size(): number { return this.entries.length; }

  step(dtMs: number): void {
    this.steps++;
    for (const s of this.sorted) s.step(dtMs);
  }

  private resort(): void { this.sorted = [...this.entries].sort((a, b) => a.order - b.order || a.seq - b.seq).map((e) => e.s); }

  private dispose(): void {
    if (!this.alive) return;
    this.alive = false;
    this.scene.events.off(SceneEvents.UPDATE, this.onUpdate);
    this.scene.events.off(SceneEvents.SHUTDOWN, this.onShutdown);
    this.scene.events.off(SceneEvents.DESTROY, this.onShutdown);
    this.entries = [];
    this.sorted = [];
    systems.delete(this.scene);
  }
}
